import { useState, useEffect, useCallback } from 'react';
import { getResidentList } from '@api/resident';
import { ResidentDetails } from '@typings/ResidentTypes';
import useAuth from '@hooks/useAuth';
import useDebounce from '@hooks/useDebounce';

const useResidentFetch = (
  partnerCarehomeId: string,
  currentPage: number,
  searchText: string
) => {
  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const { profile } = useAuth();
  const debouncedSearchText = useDebounce(searchText, 500);

  const fetchResidents = useCallback(
    async (page: number, pageSize: number, full_name: string) => {
      setLoading(true);
      if (profile) {
        const response = await getResidentList(
          partnerCarehomeId,
          page,
          pageSize,
          full_name
        );
        if (response && response.status === 'success') {
          const transformedData = response.data.data.map(
            (resident: ResidentDetails) => ({
              key: resident.id,
              user_profile: {
                ...resident.profile,
                slug: resident.slug,
                avatar: resident.avatar,
                mwNumber: `MW${resident?.profile?.mw_number}`,
                nhsNumber: resident?.profile?.nhs_number,
                is_migrated: resident.is_migrated,
              },
              residentACP: resident.profile.acp,
              mentalCapacity: resident.profile.mental_capacity,
              conversationNextOfKin: resident.profile.conversations,
              previouslySelfCompletedACP:
                resident.profile.prev_self_completed_ACP,
            })
          );
          setTableData(transformedData);
          setTotalPages(response.data.pages);
        }
        setLoading(false);
      }
    },
    [profile, partnerCarehomeId]
  );

  useEffect(() => {
    if (partnerCarehomeId) {
      fetchResidents(currentPage, 5, debouncedSearchText);
    }
  }, [
    profile,
    partnerCarehomeId,
    currentPage,
    fetchResidents,
    debouncedSearchText,
  ]);

  return { tableData, totalPages, loading };
};

export default useResidentFetch;
